import moment from 'moment'
import router from '@/router'
import { getSessionItem } from '@/utils/sessionstorage'
import { ElMessage } from 'element-plus'
import { getPageUrl } from '@/utils/menu'
import { useChannel } from '@/hooks/useChannel'

// 判断当前手机号的格式是否正确
export const modifyPhone = (value: string) => {
  const reg = /^1[23456789]\d{9}$/
  if (!value) {
    return {
      status: false,
      message: '请输入手机号码'
    }
  } else if (!reg.test(value)) {
    return {
      status: false,
      message: '手机号码错误'
    }
  } else {
    return {
      status: true
    }
  }
}

/**
 * 深拷贝
 * @param obj
 * @param map
 * @returns
 */
export const cloneDeep = (obj: any, map = new WeakMap()): any => {
  if (typeof obj !== 'object' || obj == null) return obj

  // 避免循环引用
  const objFromMap = map.get(obj)
  if (objFromMap) return objFromMap

  let target: any = {}
  map.set(obj, target)

  // Map
  if (obj instanceof Map) {
    target = new Map()
    obj.forEach((v, k) => {
      const v1 = cloneDeep(v, map)
      const k1 = cloneDeep(k, map)
      target.set(k1, v1)
    })
  }

  // Set
  if (obj instanceof Set) {
    target = new Set()
    obj.forEach((v) => {
      const v1 = cloneDeep(v, map)
      target.add(v1)
    })
  }

  // Array
  if (obj instanceof Array) {
    target = obj.map((item) => cloneDeep(item, map))
  }

  // Object
  for (const key in obj) {
    const val = obj[key]
    const val1 = cloneDeep(val, map)
    target[key] = val1
  }

  return target
}

/**
 * 千位符转化
 * @param x 传递的数字
 * @returns
 */
export const numberWithCommas = (x: { toString: () => string }) => {
  // 判断当前是否存在
  if (x && !Number.isNaN(x)) {
    // 四舍五入
    const filterNum = Number.isNaN(x) ? x : Number(x).toFixed(2)
    const strX = filterNum.toString()
    const sIndex = strX.indexOf('.')
    let transNum = strX
    let endNum = ''
    if (sIndex !== -1) {
      transNum = strX.slice(0, sIndex)
      endNum = strX.slice(sIndex)
      // 补全两位小数
      if (endNum.length === 2) {
        endNum += '0'
      }
    } else {
      endNum = '.00'
    }
    const resNum = transNum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + endNum
    return resNum
  }
  // 等于零的时候
  if (x === 0) {
    return '0.00'
  }
  return x
}

/**
 * 千位符转化
 * @param x 传递的数字
 * @returns
 */
export const thousands = (num: { toString: () => string }) => {
  if (num) {
    const str = num.toString()
    const reg = str.indexOf('.') > -1 ? /(\d)(?=(\d{3})+\.)/g : /(\d)(?=(?:\d{3})+$)/g
    return str.replace(reg, '$1,')
  }
  return ''
}

/**
 * 数字金额转中文金额展示
 *  @param n 需转换的金额
 */

export const upDigit = (n: number) => {
  const fraction = ['角', '分']
  const digit = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
  const unit = [
    ['元', '万', '亿'],
    ['', '拾', '佰', '仟']
  ]
  n = Math.abs(n)
  let s = ''
  for (let i = 0; i < fraction.length; i++) {
    s += (digit[Math.floor(n * 10 * Math.pow(10, i)) % 10] + fraction[i]).replace(/零./, '')
  }
  s = s || '整'
  n = Math.floor(n)
  for (let i = 0; i < unit[0].length && n > 0; i++) {
    let p = ''
    for (let j = 0; j < unit[1].length && n > 0; j++) {
      p = digit[n % 10] + unit[1][j] + p
      n = Math.floor(n / 10)
    }
    s = p.replace(/(零.)*零$/, '').replace(/^$/, '零') + unit[0][i] + s
  }
  return s
    .replace(/(零.)*零元/, '元')
    .replace(/(零.)+/g, '零')
    .replace(/^整$/, '零元整')
}

/**
 * 时间范围的返回值
 * @param date //时间 (接受格式：数组 数字 | 字符串)
 * @param format  转换的格式信息(默认返回年月日  ) 常用格式：YYYY-MM-DD HH:mm:ss
 * @returns
 */
export const modifyDate = (date: any[] | number | string, format = 'YYYY-MM-DD') => {
  let res: string[] | string | null = null
  if (Array.isArray(date)) {
    res = date.map((val) => {
      return moment(val).format(format)
    })
  } else {
    res = moment(date).format(format)
  }
  return res
}

/**
 * table 数据处理
 * @param val // 传递过来的值
 */
export const filterTableData = (val: string | number | null | undefined) => {
  if (val === null || val === '' || val === undefined) {
    return '--'
  }
  return val
}

/**
 * 设置净值
 *  @param num // 传递过来的值
 */
export const netWorthNum = (num: string | number) => {
  let res = num
  if (num) {
    res = Number(num).toFixed(4)
  }
  return res
}

/**
 * 设置ref滚动到顶部
 * @param num // 传递过来的值
 */
export const scrollTop = (ref: HTMLElement = document.getElementById('mainContent') as HTMLElement) => {
  if (ref) {
    ref.scrollTop = 0
  }
}

/**
 * 获取地址栏的信息
 * @param name
 * @returns string
 */
export const getLinkUrlInfo = (name: string) => {
  return (
    decodeURIComponent(
      (new RegExp('[?|&]' + name + '=' + '([^&;]+?)(&|;|$)').exec(location.href) || ['', ''])[1].replace(/\+/g, '%20')
    ) || ''
  )
}

/**
 *  获取用户权限的第一个页面
 * @param pageUrl //可以前往的页面名称
 * @returns string
 */
export const userFirstPage = (pageUrl?: string) => {
  // 前往的路由页面
  let toUrl = ''
  const userInfo = getSessionItem('zl-user-info')
  // 获取我的账户下的第一个页面
  const accountMenu = getSessionItem('zl-nav-menu')?.myAccountAllData
  const pageName = userInfo.firstPage
  // 接口限定跳路径信息
  if (pageName) {
    return getPageUrl(accountMenu, pageName)
  }

  if (Array.isArray(accountMenu) && accountMenu.length > 0) {
    if (Array.isArray(accountMenu[0].children) && accountMenu[0].children.length > 0) {
      const child = accountMenu[0].children[0]
      // 判断当前是否还存在下一级菜单
      if (Array.isArray(child.children) && child.children.length > 0) {
        toUrl = child.redirect
      } else {
        toUrl = child.path
      }
    } else {
      toUrl = accountMenu[0].path
    }
  }
  if (pageUrl && toUrl === '') {
    toUrl = pageUrl
  }

  return toUrl
}

/**
 * 登录成功之后的逻辑页面判断
 * @param data
 */
export const modifyIntoPage = (data: {
  firstUnsubmitInvestAcctType: number
  isOpen: boolean
  saveMode: string
  isAdmin: string
  isHaveAccount: boolean
}) => {
  const { firstUnsubmitInvestAcctType: type, isOpen, saveMode, isAdmin, isHaveAccount } = data

  // -1 未申请开户 -- 跳转用户开通类型
  // 0 以公司名义开户
  // 2 以产品名义开户
  // 3 仅开通管理员账号
  // 99 已经有提交的开户记录 -- 跳转投资账户
  // saveMode 1 已经保存模式设置 0 未保存
  switch (type) {
    case -1:
      router.push({
        path: '/accountOpenType'
      })
      break
    case 0:
      router.push({
        path: '/noMenu/registerMaterialPage',
        query: {
          registerType: type
        }
      })
      break
    case 2:
      router.push({
        path: '/noMenu/registerMaterialPage',
        query: {
          registerType: type
        }
      })
      break
    case 3:
      router.push({
        path: '/noMenu/registerMaterialPage',
        query: {
          registerType: type
        }
      })
      break
    case 99: // 已提交资料
      // 无权限则不进入
      if (Object.keys(getSessionItem('zl-nav-menu')).length === 0) {
        ElMessage({
          message: '您的账号未配置页面权限(登录失败)',
          type: 'warning'
        })
        break
      }

      if (isOpen) {
        const { datangChannel } = useChannel()
        // 存在投资账户
        if (saveMode === '0' && isAdmin === '1' && isHaveAccount && !datangChannel()) {
          // 管理员账户未做模式设置
          router.push({
            name: 'firstModeSetting'
          })
        } else {
          // 开户完毕
          router.push({
            path: userFirstPage()
          })
        }
      } else {
        router.push({
          path: '/systemManage/investmentAccount'
        })
      }
      break
  }
}
